<template>
  <basic-portlet title="Waveform:" icon="fa fa-signal">
    <template #portlet_actions>
      <slot name="player_portlet_actions" />
    </template>
    <div id="peaks-root">
      <collapse-text v-if="huge_file" class="performance-mode-warning" :text="`Performance mode is ${performance_mode ? `on` : `off`}`">
        <p>Performance mode prevents the red zoomable waveform from being rendered to aid page performance and responsiveness.</p>
        <p>Disabling performance mode will allow the zoomable waveform to render, but may slow down the page.</p>
        <b-button variant="ss-standout" @click="disablePerformanceMode">
          Disable
        </b-button>
      </collapse-text>
      <audio v-show="false" id="peaks-target">
        <source :src="asset.path">
      </audio>
      <div v-show="!performance_mode">
        <div id="peaks-zoom-container" />
        <hr>
      </div>
      <div id="peaks-overview-container" :class="[performance_mode ? `performance-mode` : ``]" />
      <div id="playtime-display">
        {{ playtime_display }}
      </div>
      <div id="peaks-controls">
        <div class="left-controls">
          <ul class="clean-list">
            <li>
              <b-button v-if="!playing" variant="ss-standout" @click="play">
                <i class="fa fa-play" />Play
              </b-button>
              <b-button v-else variant="ss-standout" @click="pause">
                <i class="fa fa-stop" />Pause
              </b-button>
              <b-button v-if="markerEditing" variant="ss" @click="addMarkerAtPlayhead">
                <i class="fa fa-plus" />Add marker
              </b-button>
            </li>
          </ul>
        </div>
        <div class="right-controls">
          <ul class="clean-list">
            <li>
              <label for="peaks-zoom-slider">Zoom level:</label>
              <div class="zoom-level-container">
                <i class="fa fa-search-plus" />
                <input id="peaks-zoom-slider"
                       type="range"
                       :value="0"
                       :min="0"
                       :max="zoom_levels.length - 1"
                       :step="1"
                       @input="(ev) => Math.max(1, setZoom(ev.target.value))">
              </div>
            </li>
            <li>
              <label for="peaks-volume-slider">Volume:</label>
              <div class="volume-level-container">
                <i class="fa fa-signal" />
                <input id="peaks-volume-slider"
                       type="range"
                       :value="99"
                       :min="0"
                       :max="volume_levels.length - 1"
                       :step="1"
                       @input="(ev) => setVolume(ev.target.value)">
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </basic-portlet>
</template>

<script>
import Peaks from 'peaks.js';
import { BButton } from 'bootstrap-vue';

import BasicPortlet from 'VueComponents/BasicPortlet';
import CollapseText from 'VueComponents/CollapseText';

const pair_with_peaks_marker = marker => ({
  original: marker,
  peaks_marker: {
    time: marker.time,
    editable: true,
    color: `#323232`,
    labelText: marker.name ?? `Ad`,
    id: marker.uuid,
  },
});

const peaks_to_chapter_marker = peaks_marker => ({
  time: peaks_marker._time,
  time_formatted: moment.duration(peaks_marker._time * 1000).format(`hh:mm:ss`, {
    trim: false,
  }),
  name: null,
  description: null,
  enabled: true,
  is_ad: false,
  sticky: false,
});

const sort_by_time = (m1, m2) => {
  if (m1.time < m2.time) return -1;
  else if (m1.time > m2.time) return 1;
  else return 0;
};

export default {
  components: {
    BasicPortlet,
    BButton,
    CollapseText,
  },
  props: {
    asset: {
      type: Object,
      required: true,
    },
    markers: {
      type: Array,
      required: false,
      default: () => ([]),
    },
    markerEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    peaks_instance: null,
    playing: false,
    performance_override: false,
    live_markers: [],
    marker_being_edited: null,
    player_time: 0,
  }),
  computed: {
    playtime_display() {
      const current = moment.duration(this.player_time, 'seconds');
      const max = moment.duration(this.asset.ffmpeg_details.duration, 'seconds');
      return `${current.format('hh:mm:ss.SS', {
        trim: false,
      })} / ${max.format('hh:mm:ss.SS', {
        trim: false,
      })}`;
    },
    loaded() {
      return !this.nullish(this.peaks_instance);
    },
    zoom_levels() {
      return this.sequence({
        initial: this.asset.ffmpeg_details.duration / 20,
        end: this.asset.ffmpeg_details.duration + (this.asset.ffmpeg_details.duration / 20),
        count: 20,
      }).map(scale => parseInt(scale));
    },
    volume_levels() {
      return this.sequence({
        initial: 0,
        end: 100,
        count: 100,
      });
    },
    huge_file() {
      return this.asset.ffmpeg_details.file_size > 150000000;
    },
    performance_mode() {
      return !this.performance_override && this.huge_file;
    },
    // === quick access getters ===
    player() {
      return this.peaks_instance?.player;
    },
    views() {
      const views = this.peaks_instance?.views;
      return {
        inst: views,
        zoom: views?.getView(`zoomview`),
        over: views?.getView(`overview`),
      };
    },
    segments() {
      const segments = this.peaks_instance?.segments;
      return {
        inst: segments,
        list: segments?.getSegments(),
      };
    },
    points() {
      const points = this.peaks_instance?.points;
      return {
        inst: points,
        list: points?.getPoints(),
      };
    },
    parsed_markers() {
      return this.live_markers.map(marker => pair_with_peaks_marker(marker));
    },
    containers() {
      return {
        zoom: document.getElementById(`peaks-zoom-container`),
        over: document.getElementById(`peaks-overview-container`),
      };
    },
    media_element() {
      return document.getElementById(`peaks-target`);
    },
    // === end getters ===
  },
  mounted() {
    this.init();
  },
  methods: {
    // === init ===
    async init() {
      this.live_markers = this.markers.slice(0);
      await this.peaksinit();
      this.initlisteners();
      this.initemitters();
    },
    async peaksinit() {
      const peaks_options = {
        containers: {
          // disable zoom view if in performance mode:
          zoomview: this.performance_mode ? null : this.containers.zoom,
          overview: this.containers.over,
        },
        mediaElement: this.media_element,
        dataUri: {
          json: `${this.asset.waveform_json_url}`,
        },
        keyboard: false,
        emitCueEvents: true,
        playheadTextColor: `#323232`,
        wheelMode: 'scroll',
      };

      try {
        this.peaks_instance = await Peaks.init(peaks_options, (err) => {
          if (err) {
            console.group(`Peaks init errors:`);
            console.error(err);
            console.groupEnd();
          }
        });

        const poll = setInterval(() => {
          if (this.loaded) {
            const zoom = this.views?.zoom;
            const over = this.views?.over;
            if (zoom) {
              zoom.setAmplitudeScale(0.6);
              zoom.setWaveformColor(`#08708a`);
              zoom.showPlayheadTime(true);
              this.setZoom(512);
            }
            if (over) {
              over.setAmplitudeScale(0.6);
              over.showPlayheadTime(true);
            }
            if (zoom && over) clearInterval(poll);
          }
        }, 50);

        for (const marker of this.parsed_markers) {
          this.points.inst.add({
            ...marker.peaks_marker,
            labelText: marker.original.is_ad ? `Ad` : (marker.original.name ?? `Unnamed marker`),
          });
        }
      } catch (err) {
        console.group(`Peaks post-init errors:`);
        console.error(err);
        console.groupEnd();
      }
    },
    initlisteners() {
      window.EventBus.$on(`peaks-cmd`, (ev) => {
        switch (ev.cmd) {
          case `play`:
            if (!this.nullish(ev.args.time)) { // prefer time arg
              this.player.seek(ev.args.time);
            } else if (!this.nullish(ev.args.marker)) {
              this.player.seek(ev.args.marker.time);
            }
            this.play();
            break;
        }
      });
      window.EventBus.$on(`peaks-update`, (ev) => {
        switch (ev.type) {
          case `point`:
            this.removeMarker(ev.attrs.old);
            this.addMarker(ev.attrs.new);
            break;
        }
      });
      window.EventBus.$on(`peaks-update-time`, (ev) => {
        this.removeMarker(this.live_markers.find(lm => lm.time === ev.time));
        this.addMarker({
          ...ev.marker,
        });
      });
      window.EventBus.$on(`peaks-update-name`, (ev) => {
        this.live_markers.find(lm => ev.marker.time === lm.time).name = ev.name;
        this.points.inst.removeByTime(ev.marker.time);
        this.points.inst.add({
          labelText: ev.name ?? `Unnamed marker`,
          time: ev.marker.time ?? this.player.getCurrentTime(),
          editable: true,
          color: `#323232`,
        });
      });
      window.EventBus.$on(`peaks-remove`, (ev) => {
        switch (ev.type) {
          case `point`:
            this.removeMarker(ev.attrs);
            break;
        }
      });
    },
    initemitters() {
      this.peaks_instance.on('player.timeupdate', (ev) => {
        this.player_time = ev;
        if (this.player_time === parseFloat(this.asset.ffmpeg_details.duration)) {
          this.pause();
        }
      });
      this.peaks_instance.on(`points.dragstart`, (ev) => {
        const marker = this.markers.find(marker => marker.time === ev._time);
        this.marker_being_edited = marker;
      });
      this.peaks_instance.on(`points.dragend`, (ev) => {
        const new_data = peaks_to_chapter_marker(ev);
        this.live_markers.splice(lm => lm.time === this.marker_being_edited.time, 1);
        this.live_markers.push(new_data);
        window.EventBus.$emit(`peaks-marker`, {
          action: `dragged`,
          old: this.marker_being_edited,
          new: {
            ...this.marker_being_edited,
            time: new_data.time,
            time_formatted: new_data.time_formatted,
          },
        });
        this.marker_being_edited = null;
      });
    },
    // === end init ===
    // === util ===
    disablePerformanceMode() {
      this.performance_override = true;
      const poll = setInterval(() => {
        if (this.containers.zoom.clientWidth !== 0) {
          clearInterval(poll);
          this.init();
        }
      }, 50);
    },
    setZoom(val) {
      this.views?.zoom?.setZoom({
        seconds: Math.min(this.zoom_levels[parseInt(val)], 512),
      });
    },
    setVolume(val) {
      this.media_element.volume = parseInt(val) / 100;
    },
    addMarker(marker) {
      this.live_markers.push(marker);
      this.live_markers.sort(sort_by_time);
      this.points.inst.add({
        labelText: marker.name ?? `Unnamed marker`,
        time: marker.time ?? this.player.getCurrentTime(),
        editable: true,
        color: `#323232`,
      });
      window.EventBus.$emit(`peaks-marker`, {
        action: `added`,
        marker: marker,
      });
    },
    removeMarker(marker) {
      const removal_index = this.live_markers.findIndex(m => m.time === marker.time);
      this.live_markers.splice(removal_index, 1);
      this.points.inst.removeByTime(marker.time);
      window.EventBus.$emit(`peaks-marker`, {
        action: `removed`,
        marker: marker,
      });
    },
    addMarkerAtPlayhead() {
      const marker = {
        time: this.player.getCurrentTime(),
        time_formatted: moment.duration(this.player.getCurrentTime() * 1000).format(`hh:mm:ss`, {
          trim: false,
        }),
        name: null,
        description: null,
        enabled: true,
        is_ad: false,
        sticky: false,
      };
      this.addMarker(marker);
    },
    // === player methods ===
    play() {
      this.player.play();
      this.playing = true;
    },
    pause() {
      this.player.pause();
      this.playing = false;
    },
    // === end player methods ===
  },
};
</script>

<style lang="scss" scoped>
@use "~AssetsSass/_variables" as *;
@use "~AssetsSass/ranges";

#peaks-root {
  .performance-mode-warning {
    font-size: 1.1em;
    margin: 6px 6px 12px 0;

    .btn {
      margin-right: 6px;
    }
  }

  #peaks-zoom-container {
    height: 200px;
  }

  #peaks-overview-container {
    height: 85px;
    &.performance-mode {
      height: 200px;
    }
  }

  #playtime-display {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 1em 0 2em 0;
    font-size: 1.2em;
    font-weight: bold;
  }

  #peaks-controls {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    margin-top: 18px;

    &>div {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    .left-controls {
      &>ul {
        display: flex;
        flex-direction: row;
        justify-content: space-around;
        margin: 0 12px;
        &>li {
          margin: 0 6px;
        }
      }
    }

    .right-controls {
      display: flex;
      flex-direction: column;
      align-items: center;
      &>label {
        margin-bottom: 10px;
      }
      .zoom-level-container,
      .volume-level-container {
        display: flex;
        flex-direction: row;
        #peaks-zoom-slider,
        #peaks-volume-slider {
          min-width: 30vw;
          margin: 0 10px;
        }
      }
    }
  }
}
</style>
